import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getProductDetail } from "../../api/buyerProducts.api";
import { useAuth } from "../../context/AuthContext";
import { createRFQ } from "../../api/buyerRFQ.api";
import { saveProduct } from "../../api/buyerSaved.api";
import { getReviews, addReview } from "../../api/buyerReviews.api";
import { toast } from "react-toastify";

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [tab, setTab] = useState("description");

  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [avgRating, setAvgRating] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);
  const [rating, setRating] = useState(0);
  const [reviewText, setReviewText] = useState("");
  const [showContact, setShowContact] = useState(false);
  const [rfqLoading, setRfqLoading] = useState(false);

  const [selectedImage, setSelectedImage] = useState(null);
  const [showWriteReview, setShowWriteReview] = useState(false);

  // --- STYLE CONSTANTS ---
  const primaryNavy = "#112347";
  const accentOrange = "#e67e22";
  const lightBg = "#f9fafb";

  useEffect(() => {
    if (data?.product) {
      setSelectedImage(data.product.main_image);
    }
  }, [data]);

  useEffect(() => {
    getProductDetail(id).then((res) => {
      setData(res.data.data);
    });
  }, [id]);

  useEffect(() => {
    if (id) {
      getReviews(id).then((res) => {
        setReviews(res.data.data.reviews);
        setAvgRating(res.data.data.avg_rating);
        setTotalReviews(res.data.data.total_reviews);
      });
    }
  }, [id]);


  if (!data) return <div className="p-5 text-center fw-bold">Loading product details...</div>;

  const product = data?.product || {};
const specs = data?.specs || [];

  /* RFQ, SAVE, REVIEW LOGIC */
  const handleReview = async () => {
    try {
      if (!user) {
        window.dispatchEvent(new Event("openLogin"));
        return;
      }
      if (!rating) return toast.error("Select rating");
      const res = await addReview({
        seller_product_id: Number(id),
        rating,
        review_text: reviewText,
      });
      toast.success(res.data.message);
      const r = await getReviews(id);
      setReviews(r.data.data.reviews);
      setAvgRating(r.data.data.avg_rating);
      setTotalReviews(r.data.data.total_reviews);
      setRating(0);
      setReviewText("");
      setShowWriteReview(false);
    } catch (e) {
      toast.error("Review failed");
    }
  };

  const handleRFQ = async () => {
    if (!user) {
      window.dispatchEvent(new Event("openLogin"));
      return;
    }
    if (rfqLoading) return;
    setRfqLoading(true);
    try {
      const res = await createRFQ({ seller_product_id: product.id, quantity: 1 });
      toast.success(res.data.isOk ? "RFQ Sent" : res.data.message);
    } catch (e) {
      toast.error("RFQ failed");
    } finally {
      setRfqLoading(false);
    }
  };

  const handleSave = async () => {
    if (!user) {
      window.dispatchEvent(new Event("openLogin"));
      return;
    }
    try {
      const res = await saveProduct({ seller_product_id: product.id });
      toast.success(res.data.isOk ? "Saved" : res.data.message);
    } catch (e) {
      toast.success("Save failed");
    }
  };

const additionalImages =
  product?.additional_images
    ? (
        typeof product.additional_images === "string"
          ? JSON.parse(product.additional_images)
          : product.additional_images
      )
    : [];

  return (
    <div className="bg-white" style={{ minHeight: "100vh" }}>
      {/* BREADCRUMB SECTION */}
      <div className="container py-3">
        <nav className="small text-muted d-flex flex-wrap align-items-center gap-2">
          <span style={{ cursor: "pointer" }} onClick={() => navigate("/")}>Home</span>
          <span>›</span>
          <span className="fw-bold text-dark">{product.product_name}</span>
        </nav>
      </div>

      <div className="container">
        <div className="row g-4 g-lg-5">
          {/* LEFT: IMAGES - Responsive Columns */}
          <div className="col-12 col-lg-7">
            {/* PROPERLY STYLED MAIN IMAGE */}
            <div 
              className="mb-3 d-flex align-items-center justify-content-center" 
              style={{ 
                width: "100%",
                maxWidth: "640px",
                aspectRatio: "3 / 2", // Fixed aspect ratio
                backgroundColor: selectedImage ? "#f8f6f2" : "#ef4444", // Beige bg matching product card
                border: "1px solid #e5e0d8",
                borderRadius: "12px",
                boxShadow: "inset 0 0 0 1px #f1ede6, 0 2px 8px rgba(0,0,0,0.04)", // Premium inset shadow
                overflow: "hidden" 
              }}
            >
              {selectedImage ? (
                <img
                  src={`https://aaeternitas.com/uploads/products/${selectedImage}`}
                  alt="product"
                  style={{ 
                    width: "100%", 
                    height: "100%", 
                    objectFit: "contain", 
                    imageRendering: "auto"
                  }}
                />
              ) : (
                <div className="text-white fw-bold">
                  No Image
                </div>
              )}
            </div>

            {/* PROPERLY STYLED THUMBNAILS */}
            <div className="d-flex gap-2 gap-md-3 flex-wrap">
              {[product.main_image, ...additionalImages].map((img, i) => (
                <div
                  key={i}
                  onClick={() => setSelectedImage(img)}
                  style={{
                    width: "120px",
                    aspectRatio: "3 / 2", // Consistent aspect ratio
                    backgroundColor: "#f8f6f2", // Beige bg
                    borderRadius: "10px",
                    overflow: "hidden",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: selectedImage === img ? "2px solid #112347" : "1px solid #e5e0d8",
                    boxShadow: selectedImage === img ? "0 2px 4px rgba(17, 35, 71, 0.15)" : "none",
                    transition: "all 0.2s ease"
                  }}
                >
                  <img
                    src={`https://aaeternitas.com/uploads/products/${img}`}
                    alt="thumb"
                    style={{ 
                      width: "100%", 
                      height: "100%", 
                      objectFit: "contain",
                      imageRendering: "auto"
                    }}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT: MAIN PRODUCT DETAILS - Responsive Columns */}
          <div className="col-12 col-lg-5">
            <div 
              className="badge mb-3 px-3 py-2 text-white text-wrap text-start" 
              style={{ backgroundColor: primaryNavy, borderRadius: "6px" }}
            >
              Warehousing Equipment
            </div>
            <h2 className="fw-bold mb-2" style={{ fontSize: "clamp(24px, 4vw, 32px)", color: "#000" }}>{product.product_name}</h2>
            
            <div className="d-flex flex-wrap align-items-center gap-2 mb-4">
              <span className="text-muted small">🔗 Model: <b>{product.model_number || "N/A"}</b></span>
              <span className="badge border fw-normal text-muted" style={{ fontSize: "11px" }}>🏭 Manufacturer</span>
            </div>

            {/* SELLER INFO CARD */}
            <div className="p-3 border rounded-3 mb-4 shadow-sm" style={{ backgroundColor: "#f9fafb" }}>
              <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
                <div>
                  <h6 className="fw-bold mb-1" style={{ color: primaryNavy }}>{product.business_name}</h6>
                  <span className="badge border border-success text-success bg-success-subtle px-2 mb-2" style={{ fontSize: "10px" }}>✓ Verified</span>
                </div>
                <div className="text-md-end text-start">
                  <div className="d-flex align-items-center gap-1 justify-content-md-end justify-content-start">
                    <span className="text-warning">★</span>
                    <span className="fw-bold">{avgRating.toFixed(1)}</span>
                  </div>
                  <div className="text-muted" style={{ fontSize: "11px" }}>({totalReviews} reviews)</div>
                  <div className="text-muted" style={{ fontSize: "10px" }}>
                    Member since {product.seller_created_at 
                      ? new Date(product.seller_created_at).toLocaleDateString("en-IN", {
                          year: "numeric",
                          month: "short"
                        })
                      : "N/A"}
                  </div>
                </div>
              </div>
              <div className="small text-muted mt-2 d-flex flex-column gap-1">
                <span>📍 {product.city}, {product.state}</span>
                <span>⌛ Response: &lt; 24 hours</span>
              </div>
            </div>

            {/* QUICK STATS BOXES */}
            <div className="row g-2 mb-4">
              {[
                  {
                    label: "Min. Order",
                    val: product.min_orders ? `${product.min_orders} Units` : "N/A",
                    icon: "📦"
                  },
                  {
                    label: "Response Time",
                    val: product.response_time || "N/A",
                    icon: "⌛"
                  },
                  {
                    label: "Warranty",
                    val: product.warranty_years
                      ? `${product.warranty_years} Years`
                      : "N/A",
                    icon: "🛡️"
                  },
                  {
                    label: "Certifications",
                    val: product.certifications
                      ? product.certifications.split(",").length + " Standards"
                      : "N/A",
                    icon: "📜"
                  }
                ].map((item, i) => (
                <div className="col-6" key={i}>
                  <div className="border rounded-3 p-2 d-flex flex-column flex-sm-row align-items-sm-center gap-2 h-100">
                    <span style={{ fontSize: "18px" }}>{item.icon}</span>
                    <div style={{ lineHeight: "1.2" }}>
                      <div className="text-muted text-nowrap" style={{ fontSize: "10px" }}>{item.label}</div>
                      <div className="fw-bold small">{item.val}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA BUTTONS */}
            <div className="d-flex flex-column gap-2">
              <button 
                className="btn btn-outline-dark w-100 py-2 fw-bold shadow-sm" 
                onClick={handleRFQ} 
                disabled={rfqLoading}
                style={{ borderRadius: "8px" }}
              >
                📄 {rfqLoading ? "Sending..." : "Request Formal Quote (RFQ)"}
              </button>
              <div className="d-flex gap-2">
                <button 
                  className="btn btn-outline-dark w-75 py-2 fw-bold shadow-sm" 
                  onClick={() => setShowContact(true)}
                  style={{ borderRadius: "8px" }}
                >
                  📞 Contact Seller
                </button>
                <button 
                  className="btn btn-outline-dark w-25 py-2 fw-bold shadow-sm text-nowrap" 
                  onClick={handleSave} 
                  style={{ borderRadius: "8px" }}
                >
                  ♡ Save
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* TABS & BOTTOM CONTENT SECTION */}
        <div className="mt-5 border rounded-3 overflow-hidden shadow-sm mb-5">
          <div className="d-flex gap-1 bg-light border-bottom p-2 px-3 overflow-auto" style={{ whiteSpace: "nowrap" }}>
            {["description", "specs", "certifications", "reviews"].map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`btn btn-sm px-4 py-2 border-0 rounded-pill fw-bold ${tab === t ? "bg-white text-dark shadow-sm" : "text-muted"}`}
                style={{ textTransform: "capitalize" }}
              >
                {t} {t === "reviews" ? `(${totalReviews})` : ""}
              </button>
            ))}
          </div>

          <div className="p-3 p-md-4 tab-content">
            {/* TAB: DESCRIPTION */}
            {tab === "description" && (
              <div className="text-muted small" style={{ lineHeight: "1.8" }}>
                <p>{product.seller_notes || "No description available"}</p>
              </div>
            )}

            {/* TAB: SPECIFICATIONS */}
            {tab === "specs" && (
              <div className="row g-2 g-md-3">
                {specs && specs.length > 0 ? (
                  specs.map((s, i) => (
                    <div className="col-12 col-md-6" key={i}>
                      <div
                        className="d-flex flex-wrap justify-content-between align-items-center p-3 rounded-2 h-100"
                        style={{ backgroundColor: lightBg }}
                      >
                        <span className="text-muted pe-2" style={{ fontSize: "13px" }}>
                          {s.spec_label}
                        </span>
                        <span
                          className="fw-bold text-end"
                          style={{ color: primaryNavy, fontSize: "13px" }}
                        >
                          {s.value}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <span className="text-muted">No specifications available</span>
                )}
              </div>
            )}

            {/* TAB: CERTIFICATIONS */}
            {tab === "certifications" && (
              <div className="d-flex gap-2 gap-md-3 flex-wrap">
                {product.certifications
                  ? product.certifications.split(",").map((c, i) => (
                      <span 
                        key={i} 
                        className="px-3 px-md-4 py-2 rounded-pill fw-bold" 
                        style={{ backgroundColor: "#e0f2fe", color: "#0369a1", fontSize: "13px" }}
                      >
                        {c.trim()}
                      </span>
                    ))
                  : <span className="text-muted italic">No certifications listed</span>}
              </div>
            )}

            {/* TAB: REVIEWS */}
            {tab === "reviews" && (
              <div>
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4 border-bottom pb-3">
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <span className="fs-3 fw-bold">{avgRating.toFixed(1)}</span>
                    <div className="d-flex align-items-center text-warning">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <span key={s} style={{ fontSize: "20px" }}>{s <= avgRating ? "★" : "☆"}</span>
                      ))}
                    </div>
                    <span className="text-muted small">({totalReviews} reviews)</span>
                  </div>
                  {/* Write a review button triggers form visibility */}
                  {!showWriteReview && (
                    <button 
                      className="btn fw-bold px-4 text-white" 
                      style={{ backgroundColor: accentOrange, borderRadius: "6px" }}
                      onClick={() => setShowWriteReview(true)}
                    >
                      Write a review
                    </button>
                  )}
                </div>

                {/* CONDITIONALLY RENDERED REVIEW INPUT */}
                {showWriteReview && (
                  <div className="p-3 p-md-4 mb-4 border rounded-3 bg-light shadow-sm">
                    <h6 className="fw-bold mb-3">Share Your Feedback</h6>
                    <div className="mb-3">
                      <label className="small text-muted d-block mb-1">Your Rating</label>
                      <div className="d-flex gap-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <span
                            key={star}
                            style={{ cursor: "pointer", fontSize: "24px" }}
                            className={star <= rating ? "text-warning" : "text-muted"}
                            onClick={() => setRating(star)}
                          >
                            {star <= rating ? "★" : "☆"}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="small text-muted d-block mb-1">Your Review</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        placeholder="Write your message..."
                        value={reviewText}
                        onChange={(e) => setReviewText(e.target.value)}
                        style={{ borderRadius: "8px" }}
                      />
                    </div>
                    <div className="d-flex gap-2 flex-wrap align-items-center">
                      <button className="btn text-white fw-bold px-4" onClick={handleReview} style={{ backgroundColor: accentOrange, borderRadius: "6px" }}>Submit Review</button>
                      <button className="btn btn-link text-muted text-decoration-none fw-bold" onClick={() => { setShowWriteReview(false); setRating(0); setReviewText(""); }}>Cancel</button>
                    </div>
                  </div>
                )}

                {/* REVIEWS LIST */}
                <div className="d-flex flex-column gap-3">
                  {reviews.map((r, i) => (
                    <div key={i} className="p-3 border rounded-3 bg-white shadow-sm">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          <div className="fw-bold" style={{ color: primaryNavy }}>{r.buyer_name}</div>
                          <div className="text-warning small">
                            {[1, 2, 3, 4, 5].map((s) => (
                              <span key={s}>{s <= r.rating ? "★" : "☆"}</span>
                            ))}
                          </div>
                        </div>
                        <span className="text-muted small">12 June 2024</span>
                      </div>
                      <p className="small text-muted m-0">{r.review_text}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* MODAL: CONTACT SELLER */}
      {showContact && (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.6)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 9999, padding: "20px" }}>
          <div className="bg-white p-4 position-relative shadow-lg w-100" style={{ maxWidth: "420px", borderRadius: "12px" }}>
            <span style={{ position: "absolute", top: 15, right: 20, cursor: "pointer", fontSize: "20px" }} onClick={() => setShowContact(false)}>✕</span>
            <h5 className="fw-bold mb-4" style={{ color: primaryNavy }}>Contact Seller</h5>
            <div className="d-flex flex-column gap-3">
              <div className="p-3 bg-light rounded-2 border">
                <div className="text-muted small">Business Name</div>
                <div className="fw-bold text-break">{product.business_name}</div>
              </div>
              <div className="p-3 bg-light rounded-2 border">
                <div className="text-muted small">Email Address</div>
                <div className="fw-bold text-break">{product.email}</div>
              </div>
              <div className="p-3 bg-light rounded-2 border">
                <div className="text-muted small">Phone Number</div>
                <div className="fw-bold text-break">{product.contact_number}</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetail;